import { AppIcons, Employer, employerTransform } from '@backrs_ui/common-ui';

import { appStrings as commonAppStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import {
  CareerTracksModalHeaderContent,
  CareerTracksModalBodyContent,
} from '@backrs_ui/grow-ui/src/components/ResourceWrapper/CareerTracksModalContent';
import { ResourceHeader } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/ResourceHeader';
import { ResourceWrapper } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/ResourceWrapper';
import { TheScoop } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/TheScoop';
import {
  ResourcePageSection,
  ScoopStatItem,
} from '@backrs_ui/grow-ui/src/components/ResourceWrapper/types';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  openPlaylistListStep,
  setNuggetToAdd,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';

import { useAgentContext } from './EmployerPage.agentContext';
import { useFetchOpportunitiesSectionData } from './hooks/useFetchOpportunitiesSectionData.hook';
import { CareerPathSection } from './molecules/CareerPathSection';
import { OpportunitiesSection } from './molecules/OpportunitiesSection';

const SCOOP_STRINGS = appStrings.modules.employer;

type EmployerPageProps = Readonly<{
  employer: Employer;
  handleBookmark: () => void;
  hasBookmarked: boolean;
}>;

export const EmployerPage = ({ employer, handleBookmark, hasBookmarked }: EmployerPageProps) => {
  const dispatch = useAppDispatch();

  const summaryItems = [
    {
      title: SCOOP_STRINGS.scoop.about,
      description: employer.the_scoop_about || '',
    },
    {
      title: SCOOP_STRINGS.scoop.roles,
      description: employer.the_scoop_roles || '',
    },
  ];

  const statItems: ScoopStatItem[] = [
    {
      kind: 'text',
      title: SCOOP_STRINGS.scoop.industry,
      iconName: 'Briefcase01',
      text: employer.industries[0]?.name || '',
    },
    {
      kind: 'text',
      title: SCOOP_STRINGS.scoop.track.title,
      iconName: 'Route',
      text: employer?.minimum_track ? `${employer.minimum_track} or Higher` : '',
      info: {
        kind: 'modal',
        modalProps: {
          headerContent: <CareerTracksModalHeaderContent />,
          bodyContent: <CareerTracksModalBodyContent />,
          icon: 'GitPullRequest',
        },
      },
    },
    {
      kind: 'text',
      title: SCOOP_STRINGS.scoop.size,
      iconName: 'Building08',
      text: employer.size || '',
    },
  ];

  const { opportunities, isLoading: opportunitiesLoading } = useFetchOpportunitiesSectionData(
    employer,
    [],
  );

  const CAREER_PATH_STRINGS = appStrings.modules.employer.careerPath;
  const OPPORTUNITIES_STRINGS = appStrings.modules.employer.opportunities;

  const PAGE_SECTIONS: ResourcePageSection[] = [
    {
      id: 'the-scoop',
      title: 'The Scoop',
      hideHeader: true,
      component: <TheScoop summaryItems={summaryItems} statItems={statItems} />,
    },
    {
      id: 'career-path',
      title: CAREER_PATH_STRINGS.title,
      description: CAREER_PATH_STRINGS.description(employer.name),
      headerRightComponents: [
        <LubavButton
          key="employer-career-path-lubav-btn"
          id="employer-career-path-lubav-btn"
          label="Explain this to me"
          prompt="Give me a brief, high-level summary of the Career Paths section for this employer. Comment on any patterns or trends you notice (e.g. does it lean heavily toward one industry or function? is there good variety?). Keep it conversational and concise — don't list out individual paths."
          allowTools={false}
          useGeneralKnowledge
        />,
      ],
      component: <CareerPathSection employer={employer} />,
    },
    opportunities.length > 0 && {
      id: 'opportunities',
      title: OPPORTUNITIES_STRINGS.title,
      description: OPPORTUNITIES_STRINGS.description,
      headerRightComponents: [
        <LubavButton
          key="employer-opportunities-lubav-btn"
          id="employer-opportunities-lubav-btn"
          label="Best fit for me"
          prompt="Which of this employer's opportunities are the best fit for me?"
          allowTools={false}
          useGeneralKnowledge
        />,
      ],
      component: (
        <OpportunitiesSection opportunities={opportunities} isLoading={opportunitiesLoading} />
      ),
    },
  ].filter(Boolean) as ResourcePageSection[];

  useAgentContext(employer);

  return (
    <ResourceWrapper
      header={
        <ResourceHeader
          title={employer.name}
          logoShape="rounded"
          banner_media_url={employer.banner_media_url || undefined}
          nugget={employerTransform(employer)}
          logo_media_url={employer.logo_media_url || '/images/employer-logo-default.svg'}
          city={employer.city}
          state={employer.region}
          country={employer.country}
          buttonMain={{
            label: 'Website',
            icon: 'Link01',
            disabled: !employer.corporate_website,
            onClick: () => {
              window.open(employer.corporate_website, '_blank');
            },
          }}
          buttonGroup={[
            {
              onClick: handleBookmark,
              icon: hasBookmarked ? 'BookmarkCheck' : 'Bookmark',
              tooltipHoverText: commonAppStrings.modules.actionTabs.bookmark,
            },
            {
              onClick: async () => {
                await navigator.clipboard.writeText(window.location.href);
              },
              icon: 'Share06',
              tooltipOnClickText: 'Copied!',
              tooltipHoverText: commonAppStrings.modules.actionTabs.share,
            },
            {
              onClick: () => {
                dispatch(setNuggetToAdd(employerTransform(employer)));
                dispatch(openPlaylistListStep());
              },
              icon: 'BookmarkAdd' as AppIcons,
              tooltipHoverText: commonAppStrings.modules.actionTabs.task,
            },
          ]}
          headerActions={
            <LubavButton
              id="employer-good-fit-lubav-btn"
              label="Is this a good fit?"
              prompt="Is this employer a good fit for me?"
              allowTools={false}
              useGeneralKnowledge
            />
          }
        />
      }
      sections={PAGE_SECTIONS}
      showTabs={false}
      chipLabel={employer.name}
    />
  );
};
